/**
 * Movement — v1's platformer feel, on the server.
 *
 * This is a port of `updatePlayer` (v1 `PhysicsEngine.js:258-399`) with the
 * client-only parts removed (camera, audio, particles, the `move` message) and
 * the input source swapped from "read the keyboard" to "read one InputFrame".
 *
 * The order of operations is preserved exactly, because it is load-bearing:
 * horizontal accel, then gravity with the fall/low-jump multipliers, then
 * coyote/buffer bookkeeping, then the jump, then integration. Reordering any of
 * those changes the jump arc.
 *
 * One thing v1 did that this deliberately does not: v1 derived facing from
 * velocity. Facing here is the input's yaw, rate-limited — abilities aim where
 * the player is looking, not where they are drifting.
 */
import { MAX_YAW_DELTA_PER_TICK, Button } from "@chaos/protocol";
import { DASH_GRAVITY_MULT, DASH_SPEED, DT, COYOTE_TICKS, ICE_ACCEL_MULTIPLIER, ICE_DECEL_MULTIPLIER, JUMP_BUFFER_TICKS, MAX_STEP_XZ, MAX_STEP_Y, PHYSICS, PLAYER_HALF_HEIGHT, PLAYER_RADIUS, PULL_DURATION_MS, PULL_MAX_SPEED, WALK_INPUT, } from "./constants.js";
import { findEffect, hasEffect, isStunned, speedMultiplier } from "./effects.js";
import { clamp, moveToward, turnToward } from "./math.js";
/**
 * Applies one tick of input to a player's velocity and position.
 *
 * Collision resolution happens after this, in `world.ts`, against the position
 * this produced — the same split v1 had between `updatePlayer` and
 * `checkCollisions`.
 */
export function integrateMovement(p, aux, input, env) {
    const stunned = isStunned(p);
    const dashing = hasEffect(p, "dash");
    const hovering = hasEffect(p, "hover");
    // Dash exit: the dash drove velocity to DASH_SPEED (42) every tick, and
    // nothing else in the sim moves that fast. Left alone, the 150ms/7m dash
    // tailed into ~14m of ground skid — measured carrying players clean off
    // platform edges with zero input — and seconds of coasting in the air.
    // Clamping to sprint speed makes the dash end exactly like releasing the
    // keys at a full sprint, which is the stop the rest of the tuning was
    // hand-tuned around. Edge-triggered so knockback launches are untouched.
    if (aux.wasDashing && !dashing) {
        const maxExit = PHYSICS.SPRINT_SPEED * env.characterSpeedMult * speedMultiplier(p);
        const speed = Math.sqrt(p.vx * p.vx + p.vz * p.vz);
        if (speed > maxExit && speed > 1e-6) {
            p.vx *= maxExit / speed;
            p.vz *= maxExit / speed;
        }
    }
    aux.wasDashing = dashing;
    const moveX = stunned ? 0 : input.moveX;
    const moveZ = stunned ? 0 : input.moveZ;
    // WORLD-SPACE intent. This is the branch's defining change: movement and aim
    // are independent inputs, so the direction you travel is not derived from the
    // direction you face.
    //
    // The 3rd-person build rotated the intent by `p.yaw`, because there the
    // camera WAS the facing and "forward" meant "away from the camera". Here yaw
    // is pure aim — where the mouse or the right stick points — and rotating
    // movement by it would make strafing round a target impossible: you would
    // orbit whatever you were aiming at instead of walking where you asked.
    //
    // The client sends the vector already in world space, which is trivial for it
    // because the camera never rotates.
    const dirX = moveX;
    const dirZ = moveZ;
    const inputMag = Math.sqrt(moveX * moveX + moveZ * moveZ);
    const hasInput = inputMag > 0.01;
    // v1 had a discrete sprint key. With an analog stick the same two numbers
    // become a piecewise ramp over the input MAGNITUDE: WALK_INPUT maps to
    // exactly WALK_SPEED (unmodified WASD), 1.0 to exactly SPRINT_SPEED (Shift
    // held, or a pinned stick), and below WALK_INPUT a feathered stick creeps
    // toward zero. The direction is normalized below so the ramp is the ONLY
    // thing magnitude controls — v1's two hand-tuned speeds stay reachable
    // exactly, on every device.
    const m = Math.min(1, inputMag);
    const rampSpeed = m <= WALK_INPUT
        ? PHYSICS.WALK_SPEED * (m / WALK_INPUT)
        : PHYSICS.WALK_SPEED +
            (PHYSICS.SPRINT_SPEED - PHYSICS.WALK_SPEED) * ((m - WALK_INPUT) / (1 - WALK_INPUT));
    const targetSpeed = rampSpeed * env.characterSpeedMult * speedMultiplier(p);
    const pull = findEffect(p, "pull");
    if (pull) {
        applyPull(p, pull.ax, pull.az);
    }
    else if (dashing) {
        // A dash drives velocity outright — no accel curve, no air control. That is
        // what makes it read as a commitment rather than a nudge.
        const d = findEffect(p, "dash");
        if (d) {
            p.vx = d.ax * DASH_SPEED;
            p.vz = d.az * DASH_SPEED;
        }
    }
    else {
        // `dir` carries the input magnitude (fwd/right are orthonormal, so
        // |dir| = inputMag). Dividing it out leaves a unit direction; rampSpeed
        // above already encodes the magnitude, and encoding it twice is how the
        // old code made 16 unreachable.
        const targetVelX = hasInput ? (dirX / inputMag) * targetSpeed : 0;
        const targetVelZ = hasInput ? (dirZ / inputMag) * targetSpeed : 0;
        let accel;
        if (!p.grounded) {
            accel = hasInput ? PHYSICS.AIR_ACCEL : PHYSICS.AIR_DECEL;
        }
        else if (env.onIce) {
            accel = hasInput
                ? PHYSICS.GROUND_ACCEL * ICE_ACCEL_MULTIPLIER
                : PHYSICS.GROUND_DECEL * ICE_DECEL_MULTIPLIER;
        }
        else {
            accel = hasInput ? PHYSICS.GROUND_ACCEL : PHYSICS.GROUND_DECEL;
        }
        p.vx = moveToward(p.vx, targetVelX, accel * DT);
        p.vz = moveToward(p.vz, targetVelZ, accel * DT);
    }
    applySurface(p, env.surface);
    // Gravity. The two multipliers are the whole reason v1's jump feels crisp:
    // you fall 2.2x harder than you rise, and 4x harder if you let go early.
    if (hovering) {
        p.vy = 0;
    }
    else {
        const gravity = PHYSICS.GRAVITY * env.gravityMult * (dashing ? DASH_GRAVITY_MULT : 1);
        const jumpHeld = (input.buttons & Button.Jump) !== 0;
        if (p.vy < 0) {
            p.vy += gravity * PHYSICS.FALL_MULTIPLIER * DT;
        }
        else if (p.vy > 0 && !jumpHeld) {
            p.vy += gravity * PHYSICS.LOW_JUMP_MULTIPLIER * DT;
        }
        else {
            p.vy += gravity * DT;
        }
        if (p.vy < PHYSICS.TERMINAL_VELOCITY)
            p.vy = PHYSICS.TERMINAL_VELOCITY;
    }
    stepJump(p, aux, input, stunned);
    // Integration, with the plausibility clamp. In legitimate play the clamp
    // never fires; it exists so that a NaN or a runaway impulse bounds the damage
    // to one tick instead of teleporting a player across the arena.
    const dx = clamp(p.vx * DT, -MAX_STEP_XZ, MAX_STEP_XZ);
    const dy = clamp(p.vy * DT, -MAX_STEP_Y, MAX_STEP_Y);
    const dz = clamp(p.vz * DT, -MAX_STEP_XZ, MAX_STEP_XZ);
    p.x += dx;
    p.y += dy;
    p.z += dz;
    // Grounded is re-derived by collision every tick; clearing it here is what
    // makes "walked off a ledge" start the coyote timer.
    p.grounded = false;
}
/**
 * Applies this frame's aim.
 *
 * Called by `stepWorld` BEFORE actions, not from inside `integrateMovement`.
 * It used to run here, after `stepActions`, which meant every dash and every
 * cast in a tick resolved against the PREVIOUS frame's facing: press dash on
 * the same frame you flick the camera and you dashed along the old heading,
 * while the client — which predicts with the new one — showed the right
 * direction and then got yanked back by the correction. One tick is only 33ms,
 * but a turn-then-dash is precisely how people dash, so it landed almost every
 * time.
 */
export function applyFacing(p, input, stunned) {
    if (stunned)
        return;
    // The protocol caps a single tick's turn; enforcing it here too means a
    // client that skipped sanitization still cannot snap-aim.
    p.yaw = turnToward(p.yaw, input.yaw, MAX_YAW_DELTA_PER_TICK);
}
/** Hex's Gravity Well: cover the distance to the anchor over PULL_DURATION_MS. */
function applyPull(p, ax, az) {
    const dx = ax - p.x;
    const dz = az - p.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < 1e-6) {
        p.vx = 0;
        p.vz = 0;
        return;
    }
    const speed = Math.min(dist / (PULL_DURATION_MS / 1000), PULL_MAX_SPEED);
    p.vx = (dx / dist) * speed;
    p.vz = (dz / dist) * speed;
}
function applySurface(p, surface) {
    if (!surface || !p.grounded)
        return;
    if (surface.kind === "conveyor") {
        p.vx += surface.dirX * surface.speed * DT;
        p.vz += surface.dirZ * surface.speed * DT;
    }
}
function stepJump(p, aux, input, stunned) {
    if (p.grounded)
        aux.coyoteTicks = COYOTE_TICKS;
    else if (aux.coyoteTicks > 0)
        aux.coyoteTicks -= 1;
    const jumpPressed = (input.buttons & Button.Jump) !== 0;
    // Rising edge only: holding jump does not re-buffer, so a held button cannot
    // bunny-hop off every landing without a release.
    if (jumpPressed && !aux.jumpHeld)
        aux.jumpBufferTicks = JUMP_BUFFER_TICKS;
    else if (aux.jumpBufferTicks > 0)
        aux.jumpBufferTicks -= 1;
    aux.jumpHeld = jumpPressed;
    if (stunned)
        return;
    const canJump = p.grounded || aux.coyoteTicks > 0;
    if (canJump && aux.jumpBufferTicks > 0 && !aux.isJumping) {
        p.vy = PHYSICS.JUMP_FORCE;
        aux.isJumping = true;
        aux.coyoteTicks = 0;
        aux.jumpBufferTicks = 0;
        p.grounded = false;
    }
}
/**
 * Applies the result of collision: snap to the ground, cancel downward
 * velocity, clear the jump latch. Bounce pads replace the landing with an
 * impulse instead (v1 `EntityBehaviors.js:37-44`).
 */
export function land(p, aux, groundY, surface) {
    p.y = groundY;
    if (surface && surface.kind === "bounce") {
        p.vy = surface.force;
        p.grounded = false;
        aux.isJumping = true;
        return;
    }
    p.vy = 0;
    p.grounded = true;
    aux.isJumping = false;
}
/** Wind volumes accelerate whatever is inside them, in the air or not. */
export function applyWind(p, fx, fy, fz) {
    p.vx += fx * DT;
    p.vy += fy * DT;
    p.vz += fz * DT;
}
/**
 * A map-authoring reachability test built on v1's SPATIAL_CONSTANTS.
 *
 * In v1 these numbers were prose in an agent prompt, so the agent could and did
 * generate arenas with unreachable platforms. Here a map that fails this is a
 * map that does not ship.
 */
export function isReachable(from, to, maxHeight, maxHorizontal) {
    const rise = to.y + to.hy - (from.y + from.hy);
    if (rise > maxHeight)
        return false;
    const gapX = Math.max(0, Math.abs(to.x - from.x) - (from.hx + to.hx) - PLAYER_RADIUS * 2);
    const gapZ = Math.max(0, Math.abs(to.z - from.z) - (from.hz + to.hz) - PLAYER_RADIUS * 2);
    return Math.sqrt(gapX * gapX + gapZ * gapZ) <= maxHorizontal;
}
/** The Y a player's centre sits at when standing on a given platform top. */
export function standingY(platformTopY) {
    return platformTopY + PLAYER_HALF_HEIGHT;
}
//# sourceMappingURL=movement.js.map